/**
 * RequireOperator — route wrapper that limits a page to the Operator only.
 *
 * Operator directive (IJ-054, 2026-02):
 *   "Ensure only I / Operator can access."
 *
 * Stricter than RequireAdmin. Passes only when the /auth/verify response
 * reports `is_operator === true`, which the backend sets only for the
 * env-admin identity (role === 'admin' AND username === AUTH_USERNAME).
 * DB-flagged additional admins are denied here.
 *
 * Behaviour mirrors RequireAdmin.jsx:
 *   • No token → redirect to `/`.
 *   • Token but not operator → redirect to `/`.
 *   • Token AND is_operator === true → render children.
 *   • Inflight → minimal "Verifying access…" placeholder.
 */
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { API, getToken } from '../utils/authClient';

export function RequireOperator({ children }) {
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    const token = getToken();
    if (!token) { setStatus('denied'); return; }

    let cancelled = false;
    fetch(`${API}/auth/verify`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('verify-failed'))))
      .then((data) => {
        if (!cancelled) setStatus(data?.is_operator ? 'operator' : 'denied');
      })
      .catch(() => { if (!cancelled) setStatus('denied'); });

    return () => { cancelled = true; };
  }, []);

  if (status === 'checking') {
    return (
      <div
        data-testid="require-operator-checking"
        className="flex min-h-screen items-center justify-center bg-black"
      >
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400/70">
          Verifying operator access…
        </div>
      </div>
    );
  }

  if (status === 'denied') return <Navigate to="/" replace />;

  return children;
}

export default RequireOperator;
